import { ceil } from "@suse-o11y/ceil";
import { clamp } from "@suse-o11y/clamp";
import { floor } from "@suse-o11y/floor";
import { max } from "@suse-o11y/max";
import { min } from "@suse-o11y/min";
import { round } from "@suse-o11y/round";
import { sqrt } from "@suse-o11y/sqrt";

interface CalculateCellsFitParams {
	width: number;
	height: number;
	minCellSize: number;
}

/**
 * Maximum number of square cells of side at least `minCellSize` that fit into a
 * `width x height` area.
 */
function calculateCellsFit({ width, height, minCellSize }: CalculateCellsFitParams): number {
	return max(1, floor(width / minCellSize, 0) * floor(height / minCellSize, 0));
}

/**
 * Picks the column/row counts that keep each cell as square as possible for the
 * given container dimensions and item count.
 *
 * cols / rows = width / height
 * cols * rows = count
 * => cols = sqrt((count * width) / height)
 */
function calculateProportionalColumns({
	width,
	height,
	count,
}: {
	width: number;
	height: number;
	count: number;
}): [cols: number, rows: number] {
	if (width <= 0 || height <= 0 || count <= 1) {
		return [1, 1];
	}

	const maxCols = sqrt((count * width) / height);
	const cols = min(count, round(maxCols, 0));

	if (cols === 0) {
		return [1, 1];
	}

	return [max(1, cols), max(1, ceil(count / cols, 0))];
}

const COL_PADDING_RATIO = 0.05; // 5%

/**
 * Inter-cell padding sized as {@link COL_PADDING_RATIO} of a column's width, clamped to [4, 16] and
 * rounded to the nearest multiple of 2.
 */
function calculateColsPadding(width: number, cols: number): number {
	const colWidth = width / max(1, cols);
	const padding = clamp(COL_PADDING_RATIO * colWidth, { min: 4, max: 16 });
	const step = 2;

	return round(padding / step, 0) * step;
}

interface CalculateGridLayoutParams extends CalculateCellsFitParams {
	count: number;
}

/**
 * Lays out up to `count` square-ish cells into a `width x height` area while
 * respecting `minCellSize`.
 */
function calculateGridLayout({ width, height, minCellSize, count }: CalculateGridLayoutParams): {
	cols: number;
	rows: number;
	padding: number;
} {
	const maxCells = calculateCellsFit({ width, height, minCellSize });
	const limit = maxCells < count ? maxCells - 1 : count;

	const [cols, rows] = calculateProportionalColumns({
		width,
		height,
		count: min(count, limit + 1),
	});

	return {
		cols,
		rows,
		padding: calculateColsPadding(width, cols),
	};
}

export { calculateCellsFit, calculateGridLayout };
